import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Trophy, X, Check } from "lucide-react";
import { toast } from "sonner";

const WeeklyQuizBadge = () => {
  const { user } = useAuth();
  const [quiz, setQuiz] = useState<any>(null);
  const [earned, setEarned] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [score, setScore] = useState(0);

  useEffect(() => {
    supabase
      .from("weekly_quizzes")
      .select("*")
      .eq("is_active", true)
      .order("week_start", { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setQuiz(data));
  }, []);

  useEffect(() => {
    if (!user || !quiz) return;
    supabase
      .from("user_weekly_quiz_badges")
      .select("*")
      .eq("user_id", user.id)
      .eq("quiz_id", quiz.id)
      .maybeSingle()
      .then(({ data }) => setEarned(data));
  }, [user, quiz]);

  if (!quiz) return null;
  const questions = Array.isArray(quiz.questions) ? quiz.questions : [];
  if (questions.length === 0) return null;

  const answer = async (correct: boolean) => {
    const newScore = score + (correct ? 1 : 0);
    setScore(newScore);
    if (step + 1 < questions.length) {
      setStep(step + 1);
    } else {
      const pct = Math.round((newScore / questions.length) * 100);
      if (user && pct >= (quiz.pass_score || 70)) {
        await supabase.from("user_weekly_quiz_badges").upsert(
          { user_id: user.id, quiz_id: quiz.id, score: pct },
          { onConflict: "user_id,quiz_id" }
        );
        toast.success(`🏆 You earned the ${quiz.badge_name} badge!`);
        setEarned({ score: pct });
      } else if (!user) {
        toast.info("Sign in to save your badge!");
      } else {
        toast.info(`Score: ${pct}%. Try again to earn the badge!`);
      }
      setOpen(false);
      setStep(0);
      setScore(0);
    }
  };

  return (
    <section className="container mx-auto px-4 py-3">
      <div className="bg-gradient-to-br from-violet-100 to-fuchsia-100 border-2 border-violet-200 rounded-3xl p-4 md:p-5 shadow flex items-center gap-4">
        <div className="text-4xl">{quiz.badge_emoji || "🏆"}</div>
        <div className="flex-1">
          <div className="text-violet-700 text-xs font-bold uppercase">Weekly Quiz Badge</div>
          <h3 className="text-lg font-extrabold text-gray-900">{quiz.title}</h3>
          {quiz.description && (
            <p className="text-sm text-gray-700">{quiz.description}</p>
          )}
        </div>
        {earned ? (
          <span className="inline-flex items-center gap-1 bg-emerald-400 text-white font-bold px-4 py-2 rounded-full">
            <Trophy className="w-4 h-4" /> Earned
          </span>
        ) : (
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold px-4 py-2 rounded-full shadow"
          >
            Take Quiz
          </button>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-3xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-bold text-violet-700">
                Question {step + 1} / {questions.length}
              </span>
              <button onClick={() => setOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <h4 className="text-lg font-extrabold mb-4">{questions[step].question}</h4>
            <div className="space-y-2">
              {(questions[step].options || []).map((opt: string, i: number) => (
                <button
                  key={i}
                  onClick={() => answer(i === questions[step].correct)}
                  className="w-full text-left px-4 py-3 rounded-xl border-2 border-gray-200 hover:border-amber-400 hover:bg-amber-50 font-semibold"
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default WeeklyQuizBadge;
